import Spine from '@finviz/spine'
import merge from 'lodash.merge'
import omit from 'lodash.omit'

import {
  ChartConfigChartPaneElement,
  ObjectHash,
  PaneArea,
  RequireByKey,
  TodoObjectHashAnyType,
} from '../../types/shared'
import Thumb from '../canvas/thumb'
import { CanvasElementType, ChartElementType, IndicatorType, OverlayType } from '../constants/common'
import PaneModel, { ScaleAxis } from '../models/pane'
import Quote from '../models/quote'
import { getChartLayoutSettings } from '../models/settings'

export type TodoModalConfig = TodoObjectHashAnyType
type TodoPositionTimestamps = TodoObjectHashAnyType

interface DefaultAttrs extends ObjectHash {
  positionTimestamps?: TodoPositionTimestamps
}

class Element<Attrs extends DefaultAttrs = DefaultAttrs, Model extends PaneModel = PaneModel> extends Spine.Module {
  static type: CanvasElementType | OverlayType | ChartElementType | IndicatorType = CanvasElementType.element

  static fromObject<T extends Element>(obj: T['attrs'], model: T['model']) {
    return new this(obj, model) as T
  }

  static getNumOfBarsBuffer(
    _?: RequireByKey<ChartConfigChartPaneElement, 'overlays'> | RequireByKey<ChartConfigChartPaneElement, 'period'>
  ) {
    return 0
  }

  isCreator = false
  isCreatorDialogOpen = false
  positionTimestamps: TodoPositionTimestamps = {}
  attrs: Attrs = {} as Attrs

  model: any
  _thumbs: Thumb[]

  getDefaults?(): Partial<Attrs>
  renderCross?(ctx: RenderingContext2DType): void
  renderCrossText?(
    ctx: RenderingContext2DType,
    crossIndex: number,
    getDataByCrossIndex?: (key: keyof Quote, shouldReturnRoundedString?: boolean) => string | undefined
  ): void

  renderContent?(ctx: RenderingContext2DType): void
  renderGrid?(ctx: RenderingContext2DType): void
  renderLabels?(ctx: RenderingContext2DType): void
  declare defaults?: Partial<Attrs>
  getModalConfigBase?(): TodoModalConfig
  declare modalConfig?: TodoModalConfig
  getMinMax?(): { min: number; max: number }
  setupAxis?(fx: ScaleAxis): void | { min: number; max: number }

  activeThumb: Thumb | null = null
  isMouseDown = false
  isSelected = false
  isHovered = false
  isEditInProgress = false
  declare scaled?: ObjectHash<number> | Partial<Attrs>

  declare toolsCallback: () => void

  constructor(values: Partial<Attrs>, model: Model) {
    super(values, model)
    this.model = model
    if (this.getDefaults) {
      this.set(this.getDefaults() || {})
    } else {
      this.set(this.defaults || {})
    }
    this.set(values)
    this._thumbs = []

    this.onMouseDown = this.onMouseDown.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)
    this.bind('mousedown', this.onMouseDown)
    this.bind('mousemove', this.onMouseMove)
    this.bind('mouseup', this.onMouseUp)
    this.bind('change', () => {
      // elements is optional because this.model is not Pane in all usecases, e.g. base_chart extends this element and sets Chart as model
      const element = this.model.elements?.().findByAttribute('instance', this)
      element?.trigger('change', element)
    })
  }

  get type() {
    return (this.constructor as typeof Element).type
  }

  getThumbs() {
    return this._thumbs
  }

  moveBy(_: number, __: number) {}

  setIsSelected(isSelected: boolean) {
    this.isSelected = isSelected
    this.trigger('change')
  }

  setIsHovered(isHovered: boolean) {
    this.isHovered = isHovered
    this.trigger('change')
  }

  getShouldRenderThumbs() {
    return this.isSelected || this.isHovered
  }

  getIsPaneSelection() {
    const paneModel = this.model.elements().findByAttribute('instance', this)?.pane() as PaneModel | undefined
    return paneModel?.selection === this
  }

  set(obj: Partial<Attrs>) {
    const attrsSubset = Object.keys(obj).reduce(
      (acc, key) => ({ ...acc, [key]: this.attrs[key] }),
      {} as Partial<Attrs>
    )
    if (JSON.stringify(obj) !== JSON.stringify(attrsSubset)) {
      merge(this.attrs, obj)
      if (this.getIsPaneSelection() && !this.getIsCreator()) {
        this.cachePointPositionTimestamp()
      }
      this.trigger('change', this)
    }
    return this
  }

  get<T extends keyof Attrs>(key: T) {
    return this.attrs[key]
  }

  getBoundingPointKeys = (): { x: string[]; y: string[] } | void => {}

  fx = (x: number) => this.model.scale.x(x)

  fy = (y: number) => this.model.scale.y(y)

  scale({ x, y }: { x: string[]; y: string[] }) {
    this.scaled = {}
    x.forEach((el) => {
      Object.defineProperty(this.scaled, el, {
        get: () => this.fx(this.attrs[el]),
      })
    })

    y.forEach((el) => {
      Object.defineProperty(this.scaled, el, {
        get: () => this.fy(this.attrs[el]),
      })
    })
  }

  render(_: RenderingContext2DType) {}

  renderThumbs(context: CanvasRenderingContext2D) {
    const isHoveredThumbStyle = this.isHovered && !this.isSelected
    if (isHoveredThumbStyle) {
      context.globalAlpha = 0.5
    }
    this.getThumbs().map((thumb) => thumb.render(context))
    context.globalAlpha = 1
  }

  onMouseDown(area: PaneArea) {
    this.isMouseDown = true
    this.isEditInProgress = true
    for (const thumb of this.getThumbs()) {
      if (thumb.isInArea(area)) {
        this.activeThumb = thumb
        this.activeThumb.startEditing(area)
        return
      }
    }
  }

  onMouseMove(area: PaneArea) {
    if (!area.mouseDown) {
      return
    }
    if (this.activeThumb != null) {
      this.activeThumb.moveTo(area)
      this.trigger('change')
    }
  }

  onMouseUp(_?: PaneArea) {
    this.isMouseDown = false
    this.isEditInProgress = false
    this.activeThumb = null
    if (!this.getIsCreator()) {
      this.cachePointPositionTimestamp()
    }
    this.trigger('change', this)
  }

  cachePointPositionTimestamp = () => {
    const quote = this.model?.chart().quote()
    if (quote) {
      const { x: xPointKeys } = this.getBoundingPointKeys()!
      const positionTimestamps = xPointKeys.reduce((acc, key) => {
        const positionX = this.attrs[key]
        const timeStamp = positionX && quote.getTimestampFomPositionX(positionX)
        return {
          ...acc,
          [key]: timeStamp,
        }
      }, {}) as TodoPositionTimestamps
      this.set({ positionTimestamps } as Partial<Attrs>)
    }
  }

  updateScales() {
    const quote = this.model.chart().quote()
    const { positionTimestamps } = this.attrs
    if (!quote || !positionTimestamps) {
      // positionTimestamps check is temporary to prevent app from crashing
      // caused by corrupted drawings - https://github.com/finvizhq/charts/pull/1386/files
      return
    }
    const pointPoitionsFromTimestamp = Object.entries(positionTimestamps).reduce(
      (acc, [key, timestamp]) => ({
        ...acc,
        [key]: timestamp && this.model.chart().quote().getPositionXFromTimestamp(timestamp),
      }),
      {}
    )
    this.set(pointPoitionsFromTimestamp)
  }

  thumbsAreInArea(area: PaneArea) {
    for (const thumb of this.getThumbs()) {
      if (thumb.isInArea(area)) {
        return true
      }
    }
    return false
  }

  isInArea(area: PaneArea) {
    return this.thumbsAreInArea(area)
  }

  isDrawingElementLocked() {
    return this.model.chart().chart_layout().isLockDrawingsActive
  }

  getModalConfig() {
    let config
    if (this.getModalConfigBase) {
      config = merge({}, this.getModalConfigBase())
    } else {
      config = merge({}, this.modalConfig)
    }
    if (config.title == null) {
      config.title = this.name
    }
    for (const input of config.inputs) {
      if (input.default != null) {
        input.value = input.default
      }
      if (this.attrs[input.name] != null) {
        input.value = this.attrs[input.name]
      }
    }
    return config
  }

  toObject() {
    return merge({}, this.attrs, { type: this.type })
  }

  toConfig<T extends Element>(): T['attrs'] & { type: T['type'] } {
    return this.toObject()
  }

  getAutosaveOmittedProperties() {
    return this.attrs.positionTimestamps ? Object.keys(this.attrs.positionTimestamps) : []
  }

  toAutosaveConfig() {
    // We don't want to save attrs which are stored in "position timestamps" because they are relative to timeframe for example
    // so in order to keep drawings in sync across different timeframes we can't rely on its position but we have to rely on "position timestamps" and then call updateScales to calculate correct positions for given timeframe
    return omit(this.toConfig(), this.getAutosaveOmittedProperties()) as Partial<Attrs>
  }

  replaceMyself(element: Element<Attrs, Model>) {
    setTimeout(() => {
      element.cachePointPositionTimestamp()
      this.model.elements().findByAttribute('instance', this).replace(element)
    })
  }

  toString() {
    return this.name
  }

  getIsCreator() {
    return this.isCreator
  }

  getIsCreatorDialogOpen() {
    return this.isCreatorDialogOpen
  }

  release() {
    this.cachePointPositionTimestamp()
    this.unbind()
  }

  getChartLayoutSettings() {
    return getChartLayoutSettings(this.model.chart().chart_layout())
  }
}

export default Element
